import {
	DefaultButton,
	Dialog,
	DialogFooter,
	DialogType,
	Dropdown,
	IDropdownOption,
	IconButton,
	Link,
	MessageBar,
	MessageBarType,
	PrimaryButton,
	Spinner,
	SpinnerSize,
	Stack,
	Text,
	TextField,
	TooltipHost,
	getTheme,
	mergeStyleSets,
	mergeStyles
} from "@fluentui/react"
import { useState } from "react"

export type PandaDocStatusKey =
	| "document.draft"
	| "document.sent"
	| "document.viewed"
	| "document.completed"
	| "document.approved"
	| "document.voided" //This is "Expired" in the UI
	| "document.declined"
	| "document.waiting_pay"

export type PandaDocUpdateCode = 2 | 11 | 12 //2=Completed, 11=Expired, 12=Declined

export interface PandaDocDocument {
	id: string
	name?: string | null
	status: PandaDocStatusKey
	dateLabel?: string
}

export interface PandaDocRecipientOption {
	key: string
	text: string
	email: string
	displayName?: string
}

export interface PandaDocsPanelProps {
	headerTitle?: string
	height?: number
	pandaDocs: PandaDocDocument[]
	loading?: boolean
	onRefresh?: () => void
	onUpdatePandaDocStatus?: (documentId: string, newStatus: PandaDocUpdateCode) => Promise<void>
	onOpenPandaDoc?: (doc: PandaDocDocument) => void
	recipientOptions?: PandaDocRecipientOption[]
	onGenerateSigningLink?: (documentId: string, recipientEmail: string, recipientDisplayName?: string) => Promise<string>
	onSendSigningLinkEmail?: (params: { documentId: string; companyName?: string; recipientEmail: string; recipientDisplayName?: string; signerLink: string }) => Promise<void>
	setError?: (message: string | null) => void
	setSuccess?: (message: string | null) => void
}

const theme = getTheme()

const classNames = mergeStyleSets({
	container: {
		border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
		borderRadius: 3,
		background: theme.palette.white,
		overflow: "hidden",
		width: "100%"
	},
	header: {
		padding: "10px 12px",
		borderBottom: `1px solid ${theme.palette.neutralLight}`,
		background: theme.palette.white
	},
	body: {
		padding: 12,
		background: theme.palette.neutralLighterAlt
	},
	list: {
		marginTop: 12,
		overflowY: "auto",
		paddingRight: 6
	},
	card: {
		background: theme.palette.white,
		border: `1px solid ${theme.palette.neutralLight}`,
		borderRadius: 4,
		padding: "10px 12px",
		selectors: {
			":hover": {
				borderColor: theme.palette.neutralTertiary,
				boxShadow: theme.effects.elevation4
			}
		}
	},
	docTitle: {
		fontWeight: 700
	},
	metaRow: {
		marginTop: 6,
		color: theme.palette.neutralSecondary,
		fontSize: 13
	},
	dot: {
		width: 9,
		height: 9,
		borderRadius: "50%",
		marginRight: 8,
		marginTop: 3
	},
	statusText: {
		marginRight: 10
	},
	sendLinkRow: {
		marginTop: 8
	}
})

function getStatusDisplayText(status: PandaDocStatusKey): string {
	switch (status) {
		case "document.draft":
			return "Draft"
		case "document.sent":
			return "Sent"
		case "document.viewed":
			return "Viewed"
		case "document.completed":
			return "Completed"
		case "document.approved":
			return "Approved"
		case "document.voided":
			return "Expired"
		case "document.declined":
			return "Declined"
		case "document.waiting_pay":
			return "Waiting for Payment"
		default:
			return "Unknown"
	}
}

function getStatusFromUpdateCode(code: PandaDocUpdateCode): PandaDocStatusKey {
	switch (code) {
		case 2:
			return "document.completed"
		case 11:
			return "document.voided"
		case 12:
			return "document.declined"
	}
}

function getAvailableStatusTransitions(currentStatus: PandaDocStatusKey): IDropdownOption[] {
	switch (currentStatus) {
		case "document.draft":
		case "document.voided":
			return [
				{ key: 2, text: "Completed" },
				{ key: 12, text: "Declined" }
			]
		case "document.sent":
		case "document.viewed":
			return [
				{ key: 2, text: "Completed" },
				{ key: 11, text: "Expired" },
				{ key: 12, text: "Declined" }
			]
		case "document.completed":
			return [{ key: 12, text: "Declined" }]
		case "document.waiting_pay":
			return [{ key: 12, text: "Declined" }]
		case "document.declined":
			return [{ key: 2, text: "Completed" }]
		case "document.approved":
			return []
		default:
			return []
	}
}

function statusDotColor(status: PandaDocStatusKey): string {
	switch (status) {
		case "document.draft":
			return "#c9c9c9"
		case "document.sent":
		case "document.viewed":
			return "#0078d4"
		case "document.completed":
		case "document.approved":
			return "#2aa54a"
		case "document.voided":
		case "document.declined":
			return "#bd0c0c"
		case "document.waiting_pay":
			return "#ffa500"
		default:
			return "#c9c9c9"
	}
}

function defaultOpen(doc: PandaDocDocument) {
	const url = `https://app.pandadoc.com/a/#/documents/${doc.id}`
	window.open(url, "_blank", "noopener,noreferrer")
}

export function PandaDocsPanel({
	headerTitle = "ACMG",
	height = 320,
	pandaDocs,
	loading,
	onRefresh,
	onUpdatePandaDocStatus,
	onOpenPandaDoc,
	recipientOptions,
	onGenerateSigningLink,
	onSendSigningLinkEmail,
	setError,
	setSuccess
}: PandaDocsPanelProps) {
	const listHeightClass = mergeStyles({ height })
	const [updatingDocId, setUpdatingDocId] = useState<string | null>(null)
	const [statusOverrides, setStatusOverrides] = useState<Record<string, PandaDocStatusKey>>({})
	const [linkingDocId, setLinkingDocId] = useState<string | null>(null)
	const [sendingDoc, setSendingDoc] = useState<PandaDocDocument | null>(null)
	const [selectedRecipientKey, setSelectedRecipientKey] = useState<string | undefined>()
	const [sendToEmail, setSendToEmail] = useState<string>("")
	const [sendToDisplayName, setSendToDisplayName] = useState<string>("")
	const [generatedLink, setGeneratedLink] = useState<string>("")
	const [isSendingLinkEmail, setIsSendingLinkEmail] = useState<boolean>(false)
	const [emailSendNotice, setEmailSendNotice] = useState<{ type: MessageBarType; message: string } | null>(null)

	const handleOpen = (doc: PandaDocDocument) => {
		if (onOpenPandaDoc) return onOpenPandaDoc(doc)
		defaultOpen(doc)
	}

	const handleStatusChange = async (doc: PandaDocDocument, updateCode: PandaDocUpdateCode) => {
		if (!onUpdatePandaDocStatus) return
		const previous = statusOverrides[doc.id] ?? doc.status
		const newStatus = getStatusFromUpdateCode(updateCode)
		setStatusOverrides(prev => ({ ...prev, [doc.id]: newStatus }))
		setUpdatingDocId(doc.id)
		setError?.(null)

		try {
			await onUpdatePandaDocStatus(doc.id, updateCode)
			setSuccess?.(`Status updated to ${getStatusDisplayText(newStatus)}`)
		} catch (err: any) {
			setStatusOverrides(prev => ({ ...prev, [doc.id]: previous }))
			setError?.(err?.message || "Failed to update PandaDoc status.")
		} finally {
			setUpdatingDocId(null)
		}
	}

	const closeSendLinkDialog = () => {
		setSendingDoc(null)
		setSelectedRecipientKey(undefined)
		setSendToEmail("")
		setSendToDisplayName("")
		setGeneratedLink("")
		setEmailSendNotice(null)
		setIsSendingLinkEmail(false)
	}

	const openSendLinkDialog = (doc: PandaDocDocument) => {
		setSendingDoc(doc)
		setSelectedRecipientKey(undefined)
		setSendToEmail("")
		setSendToDisplayName("")
		setGeneratedLink("")
		setEmailSendNotice(null)
	}

	const onRecipientSelectionChanged = (_event: any, option?: IDropdownOption) => {
		const selectedKey = option?.key as string | undefined
		setSelectedRecipientKey(selectedKey)
		if (!selectedKey) return
		const selectedRecipient = (recipientOptions || []).find(v => v.key === selectedKey)
		if (!selectedRecipient) return
		setSendToEmail(selectedRecipient.email)
		setSendToDisplayName(selectedRecipient.displayName || "")
	}

	const handleGenerateLink = async () => {
		if (!sendingDoc || !onGenerateSigningLink) return

		const email = sendToEmail.trim().toLowerCase()
		const displayName = sendToDisplayName.trim()
		if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
			setEmailSendNotice({ type: MessageBarType.error, message: "Enter a valid email address before generating the link." })
			return
		}

		setLinkingDocId(sendingDoc.id)
		setEmailSendNotice(null)
		try {
			const link = await onGenerateSigningLink(sendingDoc.id, email, displayName || undefined)
			setGeneratedLink(link)
			setEmailSendNotice({ type: MessageBarType.success, message: "Signing link generated. Copy and share it with the selected recipient." })
		} catch (err: any) {
			setEmailSendNotice({ type: MessageBarType.error, message: err?.message || "Failed to generate signing link." })
		} finally {
			setLinkingDocId(null)
		}
	}

	const copyGeneratedLink = async () => {
		if (!generatedLink) return
		try {
			await navigator.clipboard.writeText(generatedLink)
			setEmailSendNotice({ type: MessageBarType.success, message: "Signing link copied to clipboard." })
		} catch (err: any) {
			setEmailSendNotice({ type: MessageBarType.error, message: err?.message || "Failed to copy signing link." })
		}
	}

	const sendGeneratedLinkEmail = async () => {
		if (!generatedLink || !sendingDoc || !onSendSigningLinkEmail) return

		const email = sendToEmail.trim().toLowerCase()
		if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
			setEmailSendNotice({ type: MessageBarType.error, message: "Enter a valid recipient email before sending." })
			return
		}

		try {
			setIsSendingLinkEmail(true)
			setEmailSendNotice(null)
			await onSendSigningLinkEmail({
				documentId: sendingDoc.id,
				companyName: (sendingDoc?.name || "").trim() || undefined,
				recipientEmail: email,
				recipientDisplayName: sendToDisplayName.trim() || undefined,
				signerLink: generatedLink
			})
			setEmailSendNotice({ type: MessageBarType.success, message: "Email sent successfully." })
		} catch (err: any) {
			const message = err?.message || "Failed to send signing link email."
			setEmailSendNotice({ type: MessageBarType.error, message })
		} finally {
			setIsSendingLinkEmail(false)
		}
	}

	return (
		<div className={classNames.container}>
			<Stack horizontal verticalAlign="center" horizontalAlign="space-between" className={classNames.header}>
				<Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
					<Text styles={{ root: { fontSize: 16, opacity: 0.8 } }} aria-hidden>
						📑
					</Text>
					<Text styles={{ root: { fontWeight: 600 } }}>{headerTitle}</Text>
				</Stack>

				<Stack horizontal tokens={{ childrenGap: 6 }}>
					{onRefresh && <IconButton ariaLabel="Refresh" iconProps={{ iconName: "Refresh" }} onClick={onRefresh} />}
				</Stack>
			</Stack>

			<div className={classNames.body}>
				<div className={`${classNames.list} ${listHeightClass}`}>
					{loading ? (
						<Stack horizontalAlign="center" styles={{ root: { paddingTop: 18 } }}>
							<Spinner size={SpinnerSize.medium} label="Loading PandaDoc documents..." />
						</Stack>
					) : (
						<Stack tokens={{ childrenGap: 10 }}>
							{pandaDocs.map(doc => {
								const isUpdating = updatingDocId === doc.id
								const effectiveStatus = statusOverrides[doc.id] ?? doc.status
								const availableTransitions = getAvailableStatusTransitions(effectiveStatus)

								return (
									<div key={doc.id} className={classNames.card} role="button" tabIndex={0}>
										<Stack horizontal verticalAlign="start" horizontalAlign="space-between">
											<Stack tokens={{ childrenGap: 6 }} styles={{ root: { minWidth: 0 } }}>
												<Link className={classNames.docTitle} styles={{ root: { wordBreak: "break-word" } }} onClick={() => handleOpen(doc)}>
													{doc.name || "View Document"}
												</Link>

												<Stack horizontal verticalAlign="center" className={classNames.metaRow}>
													<span className={`${classNames.dot} ${mergeStyles({ backgroundColor: statusDotColor(effectiveStatus) })}`} />
													<span className={classNames.statusText}>{getStatusDisplayText(effectiveStatus)}</span>
													{doc.dateLabel && <span>{doc.dateLabel}</span>}
												</Stack>

												{(effectiveStatus === "document.sent" || effectiveStatus === "document.viewed") && onGenerateSigningLink && (
													<div className={`pd-no-open ${classNames.sendLinkRow}`}>
														<TooltipHost content="Generate a secure embedded signing link for a selected recipient">
															<IconButton
																iconProps={{ iconName: "Mail" }}
																text="Send Link"
																onClick={e => {
																	e.stopPropagation()
																	openSendLinkDialog(doc)
																}}
																disabled={linkingDocId === doc.id}
																styles={{
																	root: {
																		height: 28,
																		padding: "0 8px"
																	},
																	label: {
																		fontSize: 12,
																		fontWeight: 400
																	}
																}}
															/>
														</TooltipHost>
														{linkingDocId === doc.id && <Spinner size={SpinnerSize.xSmall} styles={{ root: { marginLeft: 8 } }} />}
													</div>
												)}
											</Stack>

											<Stack tokens={{ childrenGap: 6 }} styles={{ root: { width: 200 } }} className="pd-no-open">
												<Text variant="small">Status</Text>
												<Dropdown
													placeholder={getStatusDisplayText(effectiveStatus)}
													options={availableTransitions}
													disabled={isUpdating || !onUpdatePandaDocStatus || availableTransitions.length === 0}
													onChange={(_, opt) => {
														if (opt?.key) {
															const updateCode = Number(opt.key) as PandaDocUpdateCode
															handleStatusChange(doc, updateCode)
														}
													}}
												/>
												{isUpdating && (
													<Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
														Updating...
													</Text>
												)}
											</Stack>
										</Stack>
									</div>
								)
							})}

							{!pandaDocs.length && <Text styles={{ root: { color: theme.palette.neutralSecondary, padding: 8 } }}>No PandaDoc documents found for this opportunity.</Text>}
						</Stack>
					)}
				</div>
			</div>

			<Dialog
				hidden={!sendingDoc}
				onDismiss={closeSendLinkDialog}
				dialogContentProps={{
					type: DialogType.largeHeader,
					title: "Send Embedded Signing Link",
					subText: "Select an existing account contact or enter an email and display name to generate a new secure link."
				}}>
				<Stack tokens={{ childrenGap: 10 }}>
					<Dropdown
						label="Choose existing recipient (optional)"
						placeholder="Select contact"
						options={recipientOptions || []}
						selectedKey={selectedRecipientKey}
						onChange={onRecipientSelectionChanged}
					/>
					<TextField label="Recipient email" required value={sendToEmail} onChange={(_, value) => setSendToEmail(value || "")} />
					<TextField label="Display name" value={sendToDisplayName} onChange={(_, value) => setSendToDisplayName(value || "")} />
					{generatedLink && (
						<Stack tokens={{ childrenGap: 6 }}>
							<TextField label="Generated link" value={generatedLink} readOnly />
							<Stack horizontal tokens={{ childrenGap: 8 }}>
								<PrimaryButton text="Copy Link" onClick={() => void copyGeneratedLink()} />
								<DefaultButton
									text={isSendingLinkEmail ? "Sending..." : "Send via Email"}
									onClick={() => void sendGeneratedLinkEmail()}
									disabled={isSendingLinkEmail || !onSendSigningLinkEmail}
								/>
							</Stack>
							{emailSendNotice && <MessageBar messageBarType={emailSendNotice.type}>{emailSendNotice.message}</MessageBar>}
						</Stack>
					)}
				</Stack>
				<DialogFooter>
					<PrimaryButton
						text={linkingDocId && sendingDoc && linkingDocId === sendingDoc.id ? "Generating..." : "Generate Link"}
						onClick={() => void handleGenerateLink()}
						disabled={!!(linkingDocId && sendingDoc && linkingDocId === sendingDoc.id)}
					/>
					<DefaultButton text="Close" onClick={closeSendLinkDialog} />
				</DialogFooter>
			</Dialog>
		</div>
	)
}
